// RFI tab -> Procore / ACC sources: which Procore and ACC projects this project's RFIs are
// read from (back/routes/rfiFeed, models/rfiTunnel). A source reads through one of the
// project's Procore / ACC accounts - the ones set up on the project card - and the projects
// those accounts are already linked to are offered first.
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Drawer, Empty, List, Popconfirm, Select, Space, Switch, Tag, Typography, message } from "antd";
import { DeleteOutlined, PlusOutlined, SyncOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  addFeedTunnel, getFeedSources, listSourceProjects, pullFeedTunnel, removeFeedTunnel, setFeedTunnel,
} from "api/rfi/rfiApi";

const { Text } = Typography;
const PROVIDER = { procore: "Procore", acc: "ACC" };

function runLine(lr) {
  if (!lr) return "Not read yet";
  if (lr.running) return "Reading now…";
  if (!lr.at) return "Not read yet";
  const when = `Read ${moment(lr.at).fromNow()}`;
  if (lr.error) return `${when} - failed: ${lr.error}`;
  return `${when} - ${lr.found} RFI${lr.found === 1 ? "" : "s"} (${lr.pending} pending, ${lr.archived} archived${lr.added ? `, ${lr.added} new` : ""})`;
}

export default function RfiSourcesDrawer({ projectId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [dest, setDest] = useState(undefined);
  const [proj, setProj] = useState(undefined);
  const [more, setMore] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getFeedSources(projectId).then(setData).catch((e) => setError(e.message || "Could not load the sources"));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  // while a read runs, look again every few seconds
  useEffect(() => {
    if (!data || !(data.tunnels || []).some((t) => t.lastRun && t.lastRun.running)) return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [data, load]);

  const destinations = (data && data.destinations) || [];
  const picked = destinations.find((d) => String(d._id) === dest);
  const linked = (picked && picked.projects) || [];
  const options = [...linked.map((p) => ({ ...p, name: p.path || p.containerId })), ...((more || []).filter((p) => !linked.some((l) => l.containerId === p.containerId)))];

  const listAll = async () => {
    const accountId = linked[0] && linked[0].accountId;
    setBusy(true);
    try {
      const r = await listSourceProjects(projectId, dest, accountId);
      setMore(r.projects || []);
    } catch (e) { message.error(e.message || "Could not list the projects"); } finally { setBusy(false); }
  };
  const add = async () => {
    const p = options.find((o) => o.containerId === proj);
    if (!p) return;
    setBusy(true);
    try {
      await addFeedTunnel(projectId, { destination: dest, accountId: p.accountId, containerId: p.containerId, path: p.path || p.name || "" });
      message.success("Added - reading its RFIs now");
      setDest(undefined); setProj(undefined); setMore(null);
      load();
    } catch (e) { message.error(e.message || "Could not add the source"); } finally { setBusy(false); }
  };
  const act = async (fn, ok) => {
    try { await fn(); if (ok) message.success(ok); load(); } catch (e) { message.error(e.message || "That did not work"); }
  };

  const manage = !!(data && data.can && data.can.manage);
  return (
    <Drawer open width={640} title="Procore / ACC sources" onClose={onClose}>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        Every RFI on these Procore and ACC projects is read into this project once a day: open ones under Pending,
        closed and void ones under Archive. Nothing is written back to Procore or ACC.
      </Text>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <List
        loading={!data && !error}
        dataSource={(data && data.tunnels) || []}
        locale={{ emptyText: <Empty description="No sources yet" /> }}
        renderItem={(t) => (
          <List.Item
            actions={manage ? [
              <Switch key="on" size="small" checked={t.enabled} onChange={(v) => act(() => setFeedTunnel(projectId, t._id, { enabled: v }))} />,
              <Button key="pull" size="small" icon={<SyncOutlined spin={!!(t.lastRun && t.lastRun.running)} />} onClick={() => act(() => pullFeedTunnel(projectId, t._id), "Reading now")}>Read now</Button>,
              <Popconfirm key="rm" title="Stop reading this project? The RFIs already read stay." onConfirm={() => act(() => removeFeedTunnel(projectId, t._id), "Removed")}>
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>,
            ] : []}
          >
            <List.Item.Meta
              title={<Space><Tag>{PROVIDER[t.provider]}</Tag>{t.path || t.containerId}{!t.enabled && <Tag>Off</Tag>}</Space>}
              description={<span>Through {t.destinationName || "the project's account"} - <Text type={t.lastRun && t.lastRun.error ? "danger" : "secondary"}>{runLine(t.lastRun)}</Text></span>}
            />
          </List.Item>
        )}
      />
      {manage && (
        <div style={{ marginTop: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 8 }}>Add a source</Text>
          {!destinations.length ? (
            <Alert type="info" showIcon message="This project has no Procore or ACC account yet - connect one from the project card's settings first." />
          ) : (
            <Space direction="vertical" style={{ width: "100%" }}>
              <Select style={{ width: "100%" }} placeholder="Read through which account" value={dest}
                onChange={(v) => { setDest(v); setProj(undefined); setMore(null); }}
                options={destinations.map((d) => ({ value: String(d._id), label: `${d.name}${d.label ? ` - ${d.label}` : ""}` }))} />
              {dest && (
                <Space.Compact style={{ width: "100%" }}>
                  <Select style={{ width: "100%" }} showSearch optionFilterProp="label" placeholder={`The ${PROVIDER[picked.provider]} project to read RFIs from`}
                    value={proj} onChange={setProj}
                    options={options.map((o) => ({ value: o.containerId, label: o.name || o.path || o.containerId }))}
                    notFoundContent={linked.length ? null : "This account is not linked to a project yet - list its projects"} />
                  <Button onClick={listAll} loading={busy} disabled={!linked.length}>All projects</Button>
                </Space.Compact>
              )}
              <Button type="primary" icon={<PlusOutlined />} disabled={!dest || !proj} loading={busy} onClick={add}>Add and read now</Button>
            </Space>
          )}
        </div>
      )}
    </Drawer>
  );
}
